<template>
  <z-input
    v-if="type === 'input'"
    :model-value="stringValue"
    :disabled="disabled"
    clearable
    @update:model-value="emitValue"
  />
  <z-input-number
    v-else-if="type === 'number'"
    :model-value="numberValue"
    :disabled="disabled"
    @update:model-value="emitValue"
  />
  <z-date-picker
    v-else-if="type === 'date'"
    :model-value="stringValue"
    type="date"
    value-format="YYYY-MM-DD"
    :disabled="disabled"
    clearable
    @update:model-value="emitValue"
  />
  <z-select
    v-else-if="type === 'lov'"
    :model-value="lovModelValue"
    :disabled="disabled"
    :loading="lovLoading"
    clearable
    filterable
    @update:model-value="emitValue"
  >
    <z-option
      v-for="item in lovOptions"
      :key="item.value"
      :label="item.label"
      :value="item.value"
    />
  </z-select>
  <z-input-table
    v-else-if="type === 'table'"
    :model-value="tableModelValue"
    :options="tableOptions"
    :display-text="tableDisplayText"
    :disabled="disabled || tableLoading"
    clearable
    @update:model-value="emitValue"
  />
  <z-switch
    v-else-if="type === 'switch'"
    :model-value="Boolean(modelValue)"
    :disabled="disabled"
    @update:model-value="emitValue"
  />
  <z-input
    v-else
    :model-value="stringValue"
    :disabled="disabled"
    clearable
    @update:model-value="emitValue"
  />
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import ZInput from '@zoryn/components/input'
import ZInputNumber from '@zoryn/components/input-number'
import ZDatePicker from '@zoryn/components/date-picker'
import ZSelect, { ZOption } from '@zoryn/components/select'
import ZInputTable from '@zoryn/components/input-table'
import ZSwitch from '@zoryn/components/switch'
import { useRulesLov } from './use-rules-lov'
import { isDataSourcePath, resolveTableRowLabel, useRulesTable } from './use-rules-table'
import type { RuleColumnType } from './rules'
import type { InputTableOptions } from '@zoryn/components/input-table'
const props = defineProps<{
  modelValue: unknown
  type: RuleColumnType
  dataSource?: string
  /** 参数字段 key，table 类型时优先作为 valueField */
  fieldKey?: string
  /** 已选值的展示文案（未打开选值弹窗时使用） */
  valueLabel?: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: unknown]
  'update:valueLabel': [value: string | undefined]
}>()

const lovType = computed(() =>
  props.type === 'lov' ? props.dataSource : undefined
)

const { options: lovOptions, loading: lovLoading } = useRulesLov(lovType)

const tablePath = computed(() =>
  props.type === 'table' && isDataSourcePath(props.dataSource)
    ? props.dataSource
    : undefined
)
const {
  columns: tableColumns,
  rows: tableRows,
  loading: tableLoading,
  reload: reloadTableData,
} = useRulesTable(tablePath)

const tableValueField = computed(() => {
  if (props.fieldKey && tableColumns.value.some((col) => col.field === props.fieldKey)) {
    return props.fieldKey
  }
  return tableColumns.value[0]?.field ?? 'id'
})

const tableLabelField = computed(() => {
  const valueField = tableValueField.value
  const cols = tableColumns.value.filter((col) => col.field && col.field !== valueField)
  const nameCol = cols.find(
    (col) =>
      col.title?.includes('名称') ||
      col.field?.toLowerCase().includes('name') ||
      col.field?.toLowerCase().includes('title')
  )
  return nameCol?.field ?? cols[0]?.field
})

const tableDisplayText = computed(() => {
  if (props.valueLabel) {
    return props.valueLabel
  }
  if (!tablePath.value || props.modelValue == null || props.modelValue === '') {
    return undefined
  }
  return resolveTableRowLabel(
    tableRows.value,
    tableValueField.value,
    tableLabelField.value,
    props.modelValue
  )
})

function resolveCurrentTableLabel(value: unknown): string | undefined {
  if (!tablePath.value) {
    return undefined
  }
  return resolveTableRowLabel(
    tableRows.value,
    tableValueField.value,
    tableLabelField.value,
    value
  )
}

const stringValue = computed(() =>
  props.modelValue == null ? '' : String(props.modelValue)
)

const lovModelValue = computed((): string | number | undefined => {
  const value = props.modelValue
  if (value == null || value === '') {
    return undefined
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value
  }
  return String(value)
})

const tableModelValue = computed((): string | number | null | undefined => {
  const value = props.modelValue
  if (value == null || value === '') {
    return null
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value
  }
  const num = Number(value)
  return Number.isNaN(num) ? String(value) : num
})

const numberValue = computed(() => {
  if (props.modelValue == null || props.modelValue === '') {
    return undefined
  }
  const num = Number(props.modelValue)
  return Number.isNaN(num) ? undefined : num
})

const tableOptions = computed<InputTableOptions>(() => {
  if (isDataSourcePath(props.dataSource)) {
    return {
      data: tableRows.value,
      columns: tableColumns.value.length
        ? tableColumns.value
        : [{ field: tableValueField.value, title: '值', width: 160 }],
      valueField: tableValueField.value,
      autoQuery: false,
      module: '',
      beforeOpen: async () => {
        await reloadTableData()
        return true
      },
    }
  }
  return {
    module: props.dataSource ?? '',
    columns: [
      { field: 'name', title: '名称', width: 160 },
      { field: 'code', title: '编码', width: 120 },
    ],
    valueField: 'id',
  }
})

function emitValue(value: unknown) {
  emit('update:modelValue', value)
  if (props.type === 'table' && isDataSourcePath(props.dataSource)) {
    emit('update:valueLabel', resolveCurrentTableLabel(value))
    return
  }
  emit('update:valueLabel', undefined)
}

watch(
  () => [props.type, tablePath.value, props.modelValue] as const,
  ([type, path, value]) => {
    if (type !== 'table' || !path || value == null || value === '') {
      return
    }
    if (!tableRows.value.length && !tableLoading.value) {
      void reloadTableData()
    }
  },
  { immediate: true }
)

watch(
  () => [props.valueLabel, tableDisplayText.value, props.modelValue] as const,
  ([valueLabel, displayText, modelValue]) => {
    if (props.type !== 'table' || !isDataSourcePath(props.dataSource)) {
      return
    }
    if (valueLabel || modelValue == null || modelValue === '') {
      return
    }
    if (displayText) {
      emit('update:valueLabel', displayText)
    }
  }
)
</script>
